import {
  afterRenderEffect,
  booleanAttribute,
  computed,
  DestroyRef,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
} from '@angular/core';

import { injectMenuTriggerState, NgpMenuTrigger } from 'ng-primitives/menu';

import { type MenuComponent } from './menu.component';

const TRIGGER_INPUT_SELECTOR = 'input, textarea';

@Directive({
  selector: '[mlMenuTrigger]',
  hostDirectives: [
    {
      directive: NgpMenuTrigger,
      inputs: [
        'ngpMenuTriggerPlacement: mlMenuTriggerPlacement',
        'ngpMenuTriggerOffset: mlMenuTriggerOffset',
      ],
    },
  ],
  host: {
    '(click)': 'onTriggerClick()',
    '[attr.data-focus-trap]': 'retainTriggerFocus() ? "" : null',
    '[attr.data-open]': 'triggerState().open() ? "" : null',
  },
})
export class MenuTriggerDirective {
  readonly menu = input.required<MenuComponent>({ alias: 'mlMenuTrigger' });
  readonly disabled = input(false, { alias: 'mlMenuTriggerDisabled', transform: booleanAttribute });

  protected readonly triggerState = injectMenuTriggerState();
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly retainTriggerFocus = computed(() => this.menu().retainTriggerFocus());

  constructor() {
    effect(() => {
      const template = this.menu().panelTemplate();

      if (template) this.triggerState().setMenu(template);
    });

    effect(() => {
      this.triggerState().setDisabled(this.disabled() || this.menu().usesSheet());
    });

    const triggerEl = this.host.nativeElement;
    const preventToggle = (event: Event): void => {
      this.preventToggleWhileOpen(event);
    };
    triggerEl.addEventListener('click', preventToggle, true);
    this.destroyRef.onDestroy(() => triggerEl.removeEventListener('click', preventToggle, true));

    afterRenderEffect({
      write: () => {
        if (!this.triggerState().open() || !this.retainTriggerFocus()) return;

        if (this.triggerOwnsFocus()) {
          requestAnimationFrame(() => this.restoreTriggerInputFocus());
          return;
        }

        this.restoreTriggerInputFocus();
      },
    });
  }

  protected preventToggleWhileOpen(event: Event): void {
    if (this.disabled() || !this.retainTriggerFocus() || !this.triggerState().open()) return;

    const target = event.target;
    if (target instanceof Element && target.closest('button')) return;

    event.stopImmediatePropagation();
  }

  protected onTriggerClick(): void {
    if (this.disabled() || !this.menu().usesSheet()) return;

    this.menu().openSheet();
  }

  private triggerOwnsFocus(): boolean {
    const active = document.activeElement;
    return active instanceof HTMLElement && this.host.nativeElement.contains(active);
  }

  private restoreTriggerInputFocus(): void {
    if (!this.triggerState().open() || this.triggerOwnsFocus()) return;

    this.host.nativeElement
      .querySelector<HTMLInputElement | HTMLTextAreaElement>(TRIGGER_INPUT_SELECTOR)
      ?.focus();
  }
}
